import { memo, useCallback, useMemo, useState } from "react";
import { Colors } from "../../../utils/colors";
import { useDispatch } from "react-redux";
import { Icon } from "@iconify/react";
import { DropdownMenu, DropdownMenuItem } from "../../DropdownMenu";
import ConfirmDeleteModal from "../../Containers/ConfirmPopups/ConfirmDeleteModal";
import {
  archiveAllConversations,
  deleteAllVisibleConversations,
} from "../../../redux/slices/conversationSlice";
import AlertContainer from "../../Containers/AlertContainer";

type ConversationInboxSettingsDropdownProps = {
  hasNonArchivedConversations: boolean; // If true, enables archive all option
  hasAnyConversations: boolean; // If true, enables delete all option
};

const ConversationInboxSettingsDropdown = (
  props: ConversationInboxSettingsDropdownProps
) => {
  const { hasNonArchivedConversations, hasAnyConversations } = props;
  const dispatch = useDispatch();
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [confirmModal, setConfirmModal] = useState<"Archive" | "Delete" | "">(
    ""
  );
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [alertModal, setAlertModal] = useState("");

  const styles = {
    optionContainer: {
      width: 36,
      height: 36,
      borderRadius: "50%",
      cursor: "pointer",
      border: `0.5px solid ${Colors.GRAY2}`,
    },
  };

  const handleOpenChange = useCallback(
    (nextOpen: boolean, info?: { source: "trigger" | "menu" }) => {
      if (info?.source === "trigger" || nextOpen) {
        setIsOpenDropdown(nextOpen);
      }
    },
    [setIsOpenDropdown]
  );

  const handleConfirmOnPress = useCallback(async () => {
    setDeleteLoading(true);
    if (confirmModal === "Archive") {
      await dispatch(archiveAllConversations());
    } else {
      await dispatch(deleteAllVisibleConversations());
    }
    setDeleteLoading(false);
    setConfirmModal("");
  }, [confirmModal, dispatch, setConfirmModal, setDeleteLoading]);

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    const menuItems = [
      {
        title: "Archive All",
        icon: "mdi:archive-arrow-down",
        iconColor: Colors.BLACK,
        key: "archive_all",
        isDestructive: false,
        onPress: () => {
          if (hasNonArchivedConversations) {
            setConfirmModal("Archive");
          } else {
            setAlertModal("You have no conversations to archive.");
          }
          setIsOpenDropdown(false);
        },
      },
      {
        title: "Delete All",
        icon: "ion:trash",
        iconColor: Colors.BLACK,
        key: "delete_all",
        isDestructive: true,
        onPress: () => {
          if (hasAnyConversations) {
            setConfirmModal("Delete");
          } else {
            setAlertModal("You have no conversations to delete.");
          }
          setIsOpenDropdown(false);
        },
      },
    ];
    return menuItems;
  }, [hasNonArchivedConversations, hasAnyConversations]);

  return (
    <>
      <DropdownMenu
        dropdownItems={dropdownItems}
        trigger={["click"]}
        placement={"bottomRight"}
        isOpen={isOpenDropdown}
        onOpenChange={handleOpenChange}
        overlayStyle={{ width: 200 }}
      >
        <div
          className="Centering"
          style={{
            ...styles.optionContainer,
            backgroundColor: Colors.WHITE1,
          }}
        >
          <Icon
            icon={"mdi:dots-horizontal"}
            height={22}
            style={{ color: Colors.BLACK }}
          />
        </div>
      </DropdownMenu>
      {confirmModal ? (
        <ConfirmDeleteModal
          heading={`Are you sure you want to ${
            confirmModal === "Archive" ? "archive" : "delete"
          } all conversations?`}
          subtext={
            confirmModal === "Archive"
              ? "This will archive all read and unread conversations."
              : "This will delete ALL conversations. Also, you won't receive new replies from the deleted conversations until you send them a new message. You cannot undo this. This may take a moment to complete."
          }
          deleteButtonText={
            confirmModal === "Archive" ? "Archive All" : "Delete All"
          }
          hideModal={!confirmModal}
          setIsVisible={(isVisible: boolean) =>
            setConfirmModal(isVisible ? confirmModal : "")
          }
          deleteOnPress={handleConfirmOnPress}
          buttonLoading={deleteLoading}
        />
      ) : null}
      {alertModal ? (
        <AlertContainer
          headerComp={alertModal}
          closeModal={() => setAlertModal("")}
          hideModal={!alertModal}
        />
      ) : null}
    </>
  );
};

export default memo(ConversationInboxSettingsDropdown);
